import theme from "../theme";
import {makeStyles} from "@material-ui/styles";
import { Button, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import React, {useContext, useEffect, useState} from "react";
import { getCartItemPrices } from 'functions/parseSWPrices';
import {TrackingContext} from "../providers/tracking";
import ProductThumbnail from "../products/thumbnail";
import Plus from "public/images/icons/plus.svg";
import Minus from "public/images/icons/minus.svg";
import Link from "next/link";
import { renderPromotion } from '@functions/swCartLibrary';
import useCartStore from '@services/stores/CartStore';
import useSessionStore from '@services/stores/SessionStore';
import { WishlistCartItem } from '@components/checkout/WishlistCartItem';
import { getProductAnalytics } from '@tools/productAnalytics';
import { useRouter } from 'next/router';

const useStyles = makeStyles({
	item: {
		position: 'relative',
		display: 'flex',
		padding: '12px 0px',
		'&:not(:first-of-type)': {
			borderBottom: '1px solid #f3f3f5',
		},
		'&.locked': {
			pointerEvents: 'none'
		},
	},
	item__image: {
		position: 'relative',
		marginRight: 15,
		flexShrink: 0,
		width: 120,
		height: 'fit-content',
		'.compact &': {
			width: 80,
		},
		[theme.breakpoints.down('sm')]: {
			width: 90,
		},
		'&:after': {
			content: '""',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			position: 'absolute',
			boxShadow: 'inset 0 0 90px 8px #f3f3f5',
			mixBlendMode: 'multiply',
			pointerEvents: 'none'
		}
	},
	item__info: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		marginLeft: 15,
		minWidth: 0,
		[theme.breakpoints.down('xs')]: {
			width: 'calc(100% - 24px)',
			marginLeft: 0
		}
	},
	item__name: {
		fontSize: 13,
		fontWeight: 300,
		lineHeight: 1.5,
		color: '#000',
		'.compact &': {
			color: '#000',
		},
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		overflow: 'hidden'
	},
	item__variant: {
		display: 'inline-block',
		fontSize: 13,
		fontWeight: 300,
		color: '#444',
	},
	item__priceQty: {
		display: 'flex',
		justifyContent: 'space-between',
		marginTop: 'auto'
	},
	item__prices: {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		marginTop: 'auto',
		position: 'relative',
		[theme.breakpoints.down('xs')]: {
			order: 5,
			display: 'flex',
			alignItems: 'flex-end',
			marginRight: 0,
			fontSize: 13
		},
		'@media (max-width:320px)': {
			flexDirection: 'row'
		}
	},
	item__price: {
		fontSize: 13,
		lineHeight: '38px',
		color: '#222',
		'&.special': {
			color: '#ca0d0d'
		},
		'&.old': {
			position: 'absolute',
			right: '0',
			top: -16,
			textDecoration: 'line-through',
			color: '#666',
			fontSize: 11,
		},
		'.compact &': {
			lineHeight: '32px',
		}
	},
	qtyWrapper: {
		display: 'flex',
		marginTop: 'auto'
	},
	qtyButton: {
		width: 38,
		height: 38,
		minWidth: 'auto',
		padding: 10,
		textAlign: 'center',
		appearance: 'none',
		cursor: 'pointer',
		border: '1px solid #f3f3f5',
		borderRadius: 0,
		backgroundColor: '#f3f3f5',
		'&.increment': {
			borderRadius: 0
		},
		'.compact &': {
			width: 32,
			height: 32
		},
	},
	qtyIcon: {
		fontSize: 20,
		height: 20,
		width: 20,
		fontWeight: 400,
		strokeWidth: 0.35,
	},
	qtyInput: {
		width: 38,
		padding: '0.5rem 0',
		margin: 0,
		textAlign: 'center',
		fontSize: 14,
		fontFamily: theme.typography.fontFamily,
		appearance: 'textfield',
		border: 'none',
		outline: 'none',
		backgroundColor: '#f5f5f5',
		borderTop: '1px solid #f3f3f5',
		borderBottom: '1px solid #f3f3f5',
		borderRadius: 0,
		[theme.breakpoints.down('sm')]: {
			fontSize: 16
		},
		'&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
			appearance: 'none',
			margin: 0
		},
		'.compact &': {
			width: 32,
			height: 32,
			fontSize: 13
		},
	},
	item__removeButton: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		position: 'absolute',
		left: -14,
		top: 0,
		zIndex: 3,
		padding: '0px',
		background: 'rgb(234, 239, 242)',
		border: '4px solid white',
		color: '#333',
		borderRadius: '50%',
		height: 28,
		width: 28,
		cursor: 'pointer',
		transition: 'ease background 0.2s',
		'& > svg': {
			fill: '#333',
			fontSize: 14,
		},
		'&:hover': {
			background: 'white',
		},
	},
}, {name: "CheckoutCartItem"});

let updateTimeout;

/**
 *
 * @param {LineItem} item
 * @param isLocked
 * @param suspend
 * @param resume
 * @param reload
 * @param fullWidth
 * @param compact
 * @param readOnly
 * @returns {Element}
 * @constructor
 */
export default function CheckoutCartItem({
     item,
     isLocked,
     suspend,
     resume,
     reload,
     fullWidth = false,
     compact = false,
     readOnly = false,
}) {
	const formatPrice = useSessionStore((state) => state.methods.formatPrice);
	const [quantity, setQuantity] = useState(item.quantity);
	const classes = useStyles();
	const tracking = useContext(TrackingContext);
	const {updateCart, removeCartItem} = useCartStore((state) => state.methods);
	const cart = useCartStore((state) => state.cart);
	const router = useRouter();

	let prices = getCartItemPrices(item);

	useEffect(() => {
		clearTimeout(updateTimeout);
		if (item.quantity !== quantity && quantity > 0) {
			suspend(item.id);
			updateTimeout = setTimeout(async () => {
				await updateCart({itemId: item.id, quantity: quantity});
				await reload();
				resume(item.id);
			}, 300);
		}
	}, [quantity]);

	useEffect(() => {
		setQuantity(item.quantity);
	}, [item]);

	async function removeFromCart(item) {
		suspend(item.id);
		await removeCartItem({itemId: item.id});
		await reload();
		resume();

		await removeFromCartTracking(item.quantity);
	}

	async function removeFromCartTracking(quantity=1) {
		item['analytics'] = await getProductAnalytics(item.referencedId)
		tracking.actions.removeFromCart({
			id: item.payload.productNumber,
			price: item.price.unitPrice,
			discount: item.price?.listPrice?.discount,
			list: {id: 'cart', name: 'cart_decrement', location: router.asPath},
			quantity: quantity,
			analytics: item.analytics,
		});
	}

	async function addToCartTracking() {
		item['analytics'] = await getProductAnalytics(item.referencedId)

		tracking.actions.addToCart({
            id: item.payload.productNumber,
            price: item.price.unitPrice,
            discount: item.price?.listPrice?.discount,
            list: {id: 'cart', name: 'cart_increment', location: router.asPath},
			quantity: 1,
            analytics: item.analytics,
        });
	}

	function hasOptions(item) {
		return item.payload.options.length > 0;
	}

	function getVariantOption(item) {
		return item.payload.options.map((option) => {
			return option.option;
		}).join(' - ');
	}

	function getUrl() {
		return item.payload.npEnriched ? '/' + item.payload.npEnriched.seoUrls.seoPathInfo : '/detail/' + item.referencedId;
	}

	/**
	 * Item Quantity functions
	 */
	function increment() {
		setQuantity(parseInt(quantity, 10) + 1);
		addToCartTracking(quantity);
	}

	function decrement() {
		if (quantity > 1) {
			setQuantity(parseInt(quantity, 10) - 1);
			removeFromCartTracking();
		} else {
			setQuantity(0);
			removeFromCart(item);
		}
	}

	function onChange(value) {
		setQuantity(parseInt(value, 10));
	}

	return (
		<li className={[classes.item, compact ? 'compact' : '', isLocked && isLocked !== item.id ? 'locked' : ''].join(' ')}>
			<div className={classes.item__image}>
				<Link href={getUrl()}>
					<a>
						<ProductThumbnail image={item.cover} size={120} index={0}/>
					</a>
				</Link>
			</div>

			<div className={[classes.item__info, fullWidth ? 'fullWidth' : ''].join(' ')}>
				<Link href={getUrl()}>
					<a style={{ textDecoration: 'none', paddingRight: 60 }}>
						<Typography variant="body1" className={classes.item__name}>{item.label}</Typography>

						{hasOptions(item) ?
							<Typography variant="body1" className={classes.item__variant}>
								{getVariantOption(item)}
							</Typography> : ''
						}

						{ renderPromotion(item, cart) }
					</a>
				</Link>

				{
					!readOnly && <div className={classes.item__removeButton}>
						<Close className={classes.item__remove} onClick={removeFromCart.bind(this, item)} />
					</div>
				}

				<div className={classes.item__priceQty}>
					<div className={classes.qtyWrapper}>
						<Button onClick={decrement} className={[classes.qtyButton, 'decrement'].join(' ')} disabled={readOnly}><Minus
							className={classes.qtyIcon}/></Button>
						<input type="number" autoComplete="off" autofill="off" value={quantity} name={'item-input-' + item.payload.productNumber} onChange={(event) => onChange(event.target.value)}
							   className={classes.qtyInput} disabled={readOnly}/>
						<Button onClick={increment} className={[classes.qtyButton, 'increment'].join(' ')} disabled={readOnly}><Plus
							className={classes.qtyIcon}/></Button>
					</div>

					<div className={classes.item__prices}>
						{prices.isOnSale ?
							<>
								<Typography variant="body1"
											className={[classes.item__price, 'old'].join(' ')}>{formatPrice(prices.listPrice)}</Typography>
								<Typography variant="body1"
											className={[classes.item__price, 'special'].join(' ')}>{formatPrice(prices.price)}</Typography>
							</> :
							<Typography variant="body1"
										className={classes.item__price}>{formatPrice(prices.price)}</Typography>
						}
					</div>
				</div>
				<WishlistCartItem style={{marginTop: '6px'}} productId={item.referencedId}></WishlistCartItem>
			</div>
		</li>
	);
}
